// Code Next P1 — "Write it in C" inside the step editors: the session model.
//
// Pure (no React): the steps a program asks the learner to write, the hint
// ladder for each, the program assembled from what they wrote, and the draft
// they keep. The answer key is codegen (utils/codeNextKey.js); the checker is
// utils/codeNextCheck.js.
//
// Draft (student_practice_progress.draft_state, via PUT …/draft; localStorage
// for a project outside any item). Never node data:
//   { v: 2, steps: { [nodeId]: { lines, expectedHash, verdict, hintsUsed, revealed, tries } } }
// A step whose expectedHash no longer matches the key (the flow changed after
// the C was written) is "check again": its text is kept (decision 4).
import {
  buildCodeKey, expectedLinesForStep, ownLineNumbers, findNode, functionHeaderForFlow,
  localDeclarationsForFlow, globalDeclarations,
} from './codeNextKey';
import {
  isCheckedType, readStatements, printfParts, scanfPairs, setParts, kindOfSpec, show,
  ifChain, splitBlockLines, callParts, headerParts, prototypeParts, typeWords, typeC, declC,
  declsOf, declWords, declLine, splitSwitchLines,
} from './codeNextCheck';
import { sentenceFor } from './assignmentRows';
import { deriveStepLabel } from '@/components/visual-flow-builder/cardLabel';

export const DRAFT_VERSION = 2;

/** A small, stable hash of the key lines (djb2). */
export function expectedHash(lines) {
  const s = (lines || []).join('\n');
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

const isContract = (f) => f && f.flowType === 'contract';

/** A flow's steps in run order (depth first, as the flow reads). */
function flowOrder(nodes) {
  const out = [];
  const walk = (list) => (list || []).forEach((n) => {
    if (!n || n.type === 'start' || n.type === 'end') return;
    out.push(n);
    (n.outcomes || []).forEach((o) => walk(o.nodes));
    (n.branches || []).forEach((b) => walk(b.nodes));
    walk(n.loopBody);
    walk(n.afterLast);
  });
  walk(nodes);
  return out;
}

/** The resources a flow's steps can name. */
export const resourcesOf = (project, flowId) => {
  const flow = (project.flows || []).find((f) => f.id === flowId);
  return [...(project.globalResources || []), ...((flow && flow.resources) || [])];
};

/** C type text (as a flow stores it) → words: "float" → a decimal. */
export const typeTextWords = (t) => {
  const h = headerParts(`${t || 'void'} __x(void) {`);
  return h.error ? 'a value' : typeWords(h.ret);
};

/** "hra takes basic (a decimal) and gives back a decimal". */
export function flowSigWords(sig) {
  const ps = (sig.parameters || []).map((p) => `${p.name} (${typeTextWords(p.type)})`);
  const takes = ps.length ? `takes ${ps.join(', ')}` : 'takes nothing';
  const ret = typeTextWords(sig.returnType);
  return `${sig.name} ${takes} and ${ret === 'nothing' ? 'gives back nothing' : `gives back ${ret}`}`;
}

// The Resources cards are drawn by the canvas, not stored: one id for the
// globals card, and '__resources__' for whichever flow is open — so a flow's
// card step is keyed by its flow.
export const GLOBALS_CARD_ID = '__global_resources__';
export const RESOURCES_CARD_ID = '__resources__';
export const resourcesStepId = (flowId) => `${RESOURCES_CARD_ID}@${flowId}`;
/** The step a selected canvas id names, in the open flow. */
export const stepIdForSelection = (selectedId, activeFlowId) => (
  selectedId === RESOURCES_CARD_ID ? resourcesStepId(activeFlowId) : selectedId);

/** The prototype of a written first line: `float hra(float basic) {}` → `float hra(float basic);`. */
export const prototypeFromHeader = (text) => String(text || '').trim().replace(/\s*\{\s*\}?\s*$/, ';');

/** A Resources card's values, read off its declarations: [{ name, words }]. */
export function declItems(lines) {
  let ds = null;
  try { ds = declsOf(readStatements((lines || []).join('\n'))); } catch (e) { ds = null; }
  return (ds || []).map((d) => ({ name: d.name, words: `${declWords(d)}${d.init && d.init.t !== 'list' ? `, starts at ${show(d.init)}` : ''}`, line: declLine(d) }));
}

/** "Inside main — only main uses these" / "Outside every flow — every flow can use these". */
export const declWhere = (sig) => (sig.scope === 'global'
  ? 'Outside every flow — main and every flow can use these'
  : `Inside ${sig.flowName} — only ${sig.flowName} uses these`);

export function declSigWords(sig) {
  return `${declWhere(sig)}: ${declItems(sig.lines).map((d) => `${d.name} (${d.words})`).join(', ')}`;
}

/** The step's sentence, as the v2 editor and the card say it. */
export function stepSentence(node, resources = []) {
  if (!node) return '';
  if (node.flowSig) return flowSigWords(node.flowSig);
  if (node.protoSig) return `Tell main about ${node.protoSig.name} before it is written: ${flowSigWords(node.protoSig)}`;
  if (node.declSig) return declSigWords(node.declSig);
  if (node.type === 'assignment') {
    const rows = (node.assignments || []).filter(Boolean).map((a) => {
      const s = sentenceFor(a, resources);
      if (!s.target) return '';
      return s.one ? `${s.verb} ${s.target} by 1` : `${s.verb} ${s.target} ${s.joiner} ${s.value || '…'}`;
    }).filter(Boolean);
    if (rows.length) return rows.join('; ');
  }
  return deriveStepLabel(node, resources, { wordsOnly: false, v2: true }) || node.label || node.type;
}

/**
 * Every step codegen writes, entry flow first, each flow in run order.
 * → [{ nodeId, flowId, type, node, number, expectedLines, hash, writable }]
 * `writable` = the learner writes it (Set, Show, Read, Give back, Decision, Switch, Repeat, Use, Do;
 * a Resources card, a flow's first line on its Start);
 * the rest stay platform-written for now ("Code for this step comes next").
 */
export function programSteps(project, key = null) {
  const k = key || buildCodeKey(project);
  if (!k) return [];
  const flows = (project.flows || []).filter((f) => !isContract(f) && !f.includedFrom);
  const ordered = [...flows.filter((f) => f.isEntry), ...flows.filter((f) => !f.isEntry)];
  const steps = [];
  // The globals: on the Global Resources card main shows above its Start (a
  // card the canvas draws, id GLOBALS_CARD_ID).
  const entry = ordered.find((f) => f.isEntry);
  const globals = entry && globalDeclarations(project, k);
  if (globals) {
    const declSig = { scope: 'global', lines: globals.lines };
    steps.push({
      nodeId: GLOBALS_CARD_ID, flowId: entry.id, type: 'declare', node: { id: GLOBALS_CARD_ID, type: 'global_resources', declSig },
      expectedLines: globals.lines, lineNums: globals.lineNums, hash: expectedHash(globals.lines), writable: true,
    });
  }
  ordered.forEach((flow) => {
    // A flow other than main: its first line, written on its Start step.
    if (!flow.isEntry) {
      const start = (flow.nodes || []).find((n) => n && n.type === 'start');
      const head = start && functionHeaderForFlow(project, flow.id, k);
      if (head) {
        steps.push({
          nodeId: start.id, flowId: flow.id, type: 'function',
          node: { ...start, flowSig: { name: flow.name, returnType: flow.returnType, parameters: flow.parameters || [] } },
          expectedLines: head.lines, lineNums: head.lineNums, hash: expectedHash(head.lines), writable: true,
        });
      }
    }
    // Its own values (the Resources card the canvas draws after Start):
    // declared before any step uses them.
    const locals = localDeclarationsForFlow(project, flow.id, k);
    if (locals) {
      const declSig = { scope: 'local', flowName: flow.name || 'main', lines: locals.lines };
      const id = resourcesStepId(flow.id);
      steps.push({
        nodeId: id, flowId: flow.id, type: 'declare', node: { id, type: 'resources', declSig },
        expectedLines: locals.lines, lineNums: locals.lineNums, hash: expectedHash(locals.lines), writable: true,
      });
    }
    flowOrder(flow.nodes).forEach((node) => {
      if (node.type === 'resources' || node.type === 'global_resources') return;
      const lines = expectedLinesForStep(project, flow.id, node.id, k);
      if (!lines) return;
      steps.push({
        nodeId: node.id, flowId: flow.id, type: node.type, node, expectedLines: lines,
        hash: expectedHash(lines), writable: isCheckedType(node.type),
      });
    });
    // Its prototype is written for the learner (Ram, 2026-10-26): the Start
    // step shows it once their first line is right (prototypeFromHeader).
  });
  let n = 0;
  steps.forEach((s) => { if (s.writable) { n += 1; s.number = n; } });
  return steps;
}

// ── step states ──────────────────────────────────────────────────────────

export const emptyStepState = () => ({ lines: '', expectedHash: null, verdict: null, hintsUsed: 0, revealed: false, tries: 0 });

/**
 * A step's state against the CURRENT key: 'done' | 'checkAgain' | 'wrong' |
 * 'written' (not checked yet) | 'notYet'.
 */
export function stepStatus(state, step) {
  if (!state || !String(state.lines || '').trim()) return 'notYet';
  if (state.expectedHash && step && state.expectedHash !== step.hash) return 'checkAgain';
  if (state.verdict === 'match' || state.verdict === 'works') return 'done';
  if (state.verdict === 'wrong' || state.verdict === 'unreadable') return 'wrong';
  return 'written';
}

/** Canvas rings, reusing the Code Next nodeStates vocabulary. */
export function ringFor(status, isCurrent) {
  if (isCurrent) return 'active';
  if (status === 'done') return 'done';
  return null;
}

/** The draft as stored. */
export function toDraft(states) {
  const steps = {};
  Object.entries(states || {}).forEach(([id, s]) => {
    if (!s) return;
    steps[id] = {
      lines: s.lines || '', expectedHash: s.expectedHash || null, verdict: s.verdict || null,
      hintsUsed: s.hintsUsed || 0, revealed: !!s.revealed, tries: s.tries || 0,
    };
  });
  return { v: DRAFT_VERSION, steps };
}

/** A stored draft back to states. Anything else (the old panel's snapshot, junk) is no states. */
export function fromDraft(draft) {
  if (!draft || draft.v !== DRAFT_VERSION || !draft.steps || typeof draft.steps !== 'object') return {};
  const out = {};
  Object.entries(draft.steps).forEach(([id, s]) => {
    if (!s || typeof s !== 'object') return;
    out[id] = { ...emptyStepState(), ...s, lines: String(s.lines || '') };
  });
  return out;
}

const LS_PREFIX = 'f2c:codenext:v2:';
export const localDraftKey = (projectId) => `${LS_PREFIX}${projectId || 'local'}`;

export function readLocalDraft(projectId) {
  try {
    const raw = window.localStorage.getItem(localDraftKey(projectId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function writeLocalDraft(projectId, draft) {
  try { window.localStorage.setItem(localDraftKey(projectId), JSON.stringify(draft)); } catch (e) { /* private mode */ }
}

// ── assembly ─────────────────────────────────────────────────────────────

/**
 * The learner's program: codegen's program with each written step's lines
 * replaced by theirs; a step not written yet is a grey placeholder
 * `/* step 6: write this *\/`. Single-file C only (what Run compiles).
 *
 * → { code, segments: [{ text, kind: 'wrapper'|'student'|'placeholder', nodeId? }], complete, missing }
 */
export function assembleProgram(key, steps, states) {
  if (!key || !key.singleFile) return { code: '', segments: [], complete: false, missing: [] };
  const lines = key.lines;
  const startOf = new Map(); // line number → step
  const skip = new Set();
  const headerAt = new Map(); // a block step's own lines (if / else / }) → { step, i }
  (steps || []).filter((s) => s.writable).forEach((s) => {
    const nums = s.lineNums || ownLineNumbers(s.node, key.nodeLineMap) || [];
    if (!nums.length) return;
    // A Decision's own lines sit around the steps inside it: each of the
    // learner's header lines goes where codegen's is.
    const apart = nums.some((n, i) => i > 0 && n !== nums[i - 1] + 1);
    if (apart) {
      nums.forEach((n, i) => headerAt.set(n, { step: s, i, count: nums.length }));
      return;
    }
    startOf.set(nums[0], s);
    nums.slice(1).forEach((n) => skip.add(n));
  });
  const headersOf = new Map(); // nodeId → the learner's header lines, when they fit
  const segments = [];
  const missing = [];
  for (let n = 1; n <= lines.length; n += 1) {
    if (skip.has(n)) continue;
    const head = headerAt.get(n);
    if (head) {
      const { step, i, count } = head;
      const indent = (lines[n - 1].match(/^\s*/) || [''])[0];
      const st = states && states[step.nodeId];
      const mine = st && String(st.lines || '').trim() ? String(st.lines) : null;
      if (!headersOf.has(step.nodeId)) {
        const split = mine ? (step.type === 'switch' ? splitSwitchLines(mine) : splitBlockLines(mine)) : null;
        headersOf.set(step.nodeId, split && split.length === count ? split : null);
        if (!mine) missing.push(step.nodeId);
      }
      const mineLines = headersOf.get(step.nodeId);
      if (mineLines) segments.push({ text: `${indent}${mineLines[i]}`, kind: 'student', nodeId: step.nodeId });
      else if (mine && i === 0) segments.push({ text: `${indent}${mine.trim().replace(/\n\s*/g, ' ')}`, kind: 'student', nodeId: step.nodeId });
      else if (!mine && i === 0) segments.push({ text: `${indent}/* step ${step.number}: write this */`, kind: 'placeholder', nodeId: step.nodeId });
      continue;
    }
    const step = startOf.get(n);
    if (!step) { segments.push({ text: lines[n - 1], kind: 'wrapper' }); continue; }
    const indent = (lines[n - 1].match(/^\s*/) || [''])[0];
    const st = states && states[step.nodeId];
    const mine = st && String(st.lines || '').trim() ? String(st.lines).replace(/\s+$/, '') : null;
    if (mine) {
      // A flow's first line is written with an empty body, `float hra(float basic) {}`:
      // in the program its body follows, so the {} stands as {.
      const text = step.type === 'function' ? mine.replace(/\{\s*\}\s*$/, '{') : mine;
      text.split('\n').forEach((l) => segments.push({ text: `${indent}${l.replace(/^\s+/, '')}`, kind: 'student', nodeId: step.nodeId }));
    } else {
      missing.push(step.nodeId);
      segments.push({ text: `${indent}/* step ${step.number}: write this */`, kind: 'placeholder', nodeId: step.nodeId });
    }
  }
  return { code: segments.map((s) => s.text).join('\n'), segments, complete: missing.length === 0, missing };
}

// ── hints ────────────────────────────────────────────────────────────────

const safeRead = (lines) => { try { return readStatements((lines || []).join('\n')); } catch (e) { return []; } };

/**
 * The hint ladder: 1 what it does → 2 a pattern with blanks → 3 the specific
 * fact → 4 "Show me" (the key's lines).
 */
export function hintLadder(step, resources = []) {
  if (!step) return [];
  const stmts = safeRead(step.expectedLines);
  const sentence = stepSentence(step.node, resources);
  const showMe = step.expectedLines.join('\n');
  if (step.type === 'input') {
    const sc = scanfPairs(stmts);
    const pairs = (sc && sc.pairs) || [];
    const names = pairs.map((p) => show(p.arg.t === 'addr' ? p.arg.x : p.arg));
    return [
      `This step reads ${names.length === 1 ? 'a value' : `${names.length} values`} typed at the keyboard into ${names.join(', ')}. In C, scanf reads a value and stores it where the variable lives.`,
      pairs.map((p) => `scanf("%_", ${p.arg.t === 'addr' ? '&' : ''}____);`).join('\n'),
      pairs.map((p, i) => {
        const fact = `${names[i]} is ${kindOfSpec(p.spec)}: ${p.spec.skipWs ? `" ${p.spec.raw}"` : p.spec.raw}`;
        return p.arg.t === 'addr' ? `${fact}, with & before it` : `${fact}, no & (text is already an address)`;
      }).join('\n'),
      showMe,
    ];
  }
  if (step.type === 'output') {
    const pf = stmts.map(printfParts).filter(Boolean);
    const pattern = pf.map((p) => {
      const words = p.split.text.replace(/\u0001/g, '%_').replace(/\n/g, '\\n');
      return p.args.length ? `printf("${words}", ${p.args.map(() => '____').join(', ')});` : `printf("${words}");`;
    }).join('\n');
    const facts = [];
    pf.forEach((p) => p.split.specs.forEach((s, i) => {
      const name = show(p.args[i]);
      facts.push(s.precision !== null
        ? `${name} is shown with ${s.precision} decimal${s.precision === '1' ? '' : 's'}: ${s.raw}`
        : `${name} is ${kindOfSpec(s)}: ${s.raw}`);
    }));
    if (pf.some((p) => p.split.text.endsWith('\n'))) facts.push('\\n at the end starts a new line');
    return [
      `This step prints a message on the screen${sentence ? ` (${sentence})` : ''}. In C, printf prints text; each value goes in as a % code, and the values follow after a comma.`,
      pattern,
      facts.join('\n') || 'Only words: no % codes.',
      showMe,
    ];
  }
  if (step.type === 'assignment') {
    const parts = stmts.map(setParts).filter(Boolean);
    return [
      `This step stores a new value${parts.length ? ` in ${parts.map((p) => p.targetText).join(', ')}` : ''}${sentence ? ` — "${sentence}"` : ''}. In C, the variable goes on the left, the value on the right, and the line ends with ;`,
      parts.map((p) => `____ ${p.op} ____;`).join('\n') || '____ = ____;',
      parts.map((p) => (p.op === '=' ? `${p.targetText} gets a new value: =` : `"${p.op === '+=' ? 'Increase' : p.op === '-=' ? 'Decrease' : 'Change'} ${p.targetText} by …" is ${p.op} (or ${p.targetText} = ${p.targetText} ${p.op[0]} …)`)).join('\n'),
      showMe,
    ];
  }
  if (step.type === 'decision') {
    const ch = stmts.length === 1 ? ifChain(stmts[0]) : null;
    const conds = ch ? ch.conds : [];
    const shape = conds.map((_, k) => (k === 0 ? 'if (____) {' : '} else if (____) {'));
    if (ch && ch.hasElse) shape.push('} else {');
    shape.push('}');
    return [
      `This step chooses which steps run${sentence ? ` (${sentence})` : ''}. In C, if (check) { } runs what is inside when the check is true; else if (check) tries the next check; else { } catches everything else. Write only these lines — the steps inside are written on their own.`,
      shape.join('\n'),
      [...conds.map((c, k) => `${k === 0 ? 'The if' : `The ${['1st', '2nd', '3rd'][k - 1] || `${k}th`} else if`} checks ${show(c)}`),
        ch && ch.hasElse ? 'Else: } else {' : 'No Else: no else'].join('\n'),
      showMe,
    ];
  }
  if (step.type === 'declare') {
    const items = declItems(step.expectedLines);
    const global = step.node && step.node.declSig && step.node.declSig.scope === 'global';
    return [
      global
        ? 'These values are declared outside every flow, above main, so main and every flow can use them. In C a declaration is the type, then the name, then ; — and = with a value if it starts with one.'
        : `These are the values ${step.node.declSig.flowName} uses. In C every value is declared before a step uses it: the type, then the name, then ; — a list adds its size in [ ], and = gives a start value.`,
      items.map((d) => (/\[/.test(d.line) ? '____ ____[__];' : / = /.test(d.line) ? '____ ____ = ____;' : '____ ____;')).join('\n'),
      items.map((d) => `${d.name} is ${d.words}: ${d.line}`).join('\n'),
      showMe,
    ];
  }
  if (step.type === 'prototype') {
    const h = prototypeParts(step.expectedLines.join('\n'));
    if (!h.error) {
      return [
        `main uses ${h.name}, but ${h.name} is written after main — and C reads from the top down. So above main, the prototype tells C about ${h.name} first: the same line as ${h.name}'s first line, ending with ; instead of {.`,
        `____ ${h.name}(${h.params.map(() => '____ ____').join(', ') || 'void'});`,
        [`It gives back ${typeWords(h.ret)}: ${typeC(h.ret)}`,
          ...h.params.map((p) => `${p.name} is ${typeWords(p.type)}: ${declC(p)}`), 'It ends with ;'].join('\n'),
        showMe,
      ];
    }
  }
  if (step.type === 'function') {
    const h = headerParts(step.expectedLines.join('\n'));
    if (!h.error) {
      const blanks = h.params.map(() => '____ ____').join(', ') || 'void';
      return [
        `This is where your flow ${h.name} starts in C. A flow is a function: write it with what it gives back, its name, and the values it takes (each with its type), then an empty body { }. The steps inside are written on their own.`,
        `____ ${h.name}(${blanks}) {}`,
        [`It gives back ${typeWords(h.ret)}: ${typeC(h.ret)}`,
          ...h.params.map((p) => `${p.name} is ${typeWords(p.type)}: ${declC(p)}`)].join('\n'),
        `${showMe.replace(/\{\s*$/, '')}{}`.replace(/\)\{\}$/, ') {}'),
      ];
    }
  }
  if (step.type === 'subflow' || step.type === 'action') {
    const u = stmts.length === 1 ? callParts(stmts[0]) : null;
    if (u && step.type === 'action') {
      const blanks = u.args.map(() => '____').join(', ');
      return [
        `This step uses the C library function ${u.fn}${sentence ? ` — "${sentence}"` : ''}${u.keep ? `, and keeps what it gives back in ${u.keepText}` : ''}. In C you call it by its name, with the values it works on inside ( ), separated by commas.`,
        `${u.keep ? '____ = ' : ''}${u.fn}(${blanks});`,
        [u.args.length ? `The value${u.args.length === 1 ? '' : 's'}, in order: ${u.args.map(show).join(', ')}` : 'It takes no values',
          u.keep ? `What it gives back goes into ${u.keepText}` : ''].filter(Boolean).join('\n'),
        showMe,
      ];
    }
    if (u) {
      const blanks = u.args.map(() => '____').join(', ');
      const facts = [];
      if (u.args.length) facts.push(`The value${u.args.length === 1 ? '' : 's'} that go${u.args.length === 1 ? 'es' : ''} in, in order: ${u.args.map(show).join(', ')}`);
      u.args.filter((a) => a.t === 'addr').forEach((a) => facts.push(`&${show(a.x)}: ${u.fn} changes ${show(a.x)}, so it gets where ${show(a.x)} lives`));
      facts.push(u.keep ? `What it gives back goes into ${u.keepText}` : `Nothing it gives back is kept`);
      return [
        `This step runs your flow ${u.fn}${u.keep ? ` and keeps what it gives back in ${u.keepText}` : ''}. In C, a flow is a function: use it by its name, with the values that go in inside ( ), separated by commas.`,
        `${u.keep ? '____ = ' : ''}${u.fn}(${blanks});`,
        facts.join('\n'),
        showMe,
      ];
    }
  }
  if (step.type === 'switch') {
    const sw = stmts[0] && stmts[0].s === 'switch' ? stmts[0] : null;
    if (sw) {
      const items = (sw.body && sw.body.items) || [];
      const shape = ['switch (____) {', ...items.filter((x) => x.s !== 'empty').map((x) => (
        x.s === 'case' ? '    case __:' : x.s === 'default' ? '    default:' : '        break;')), '}'];
      const facts = [`It checks ${show(sw.e)}`];
      items.forEach((x, i) => {
        if (x.s === 'case') {
          // The step's own lines hold no steps: what follows a case is its break, or the next path.
          const next = items.slice(i + 1).find((y) => y.s !== 'empty');
          facts.push(`is ${show(x.v)}: case ${show(x.v)}:${next && next.s === 'break' ? ' — stop here: break;' : ' — runs on: no break'}`);
        }
        if (x.s === 'default') facts.push('Otherwise: default:');
      });
      return [
        `This step picks a path by matching ${show(sw.e)}${sentence ? ` (${sentence})` : ''}. In C, switch (value) { } holds the paths: case 2: starts the path for 2, break; ends it — that is "stop here" — and default: is Otherwise. Write only these lines — the steps inside are written on their own.`,
        shape.join('\n'),
        facts.join('\n'),
        showMe,
      ];
    }
  }
  if (step.type === 'loop') {
    const l = stmts[0];
    if (l && l.s === 'for') {
      const d = l.init && l.init.s === 'decl' && l.init.decls[0];
      const counter = d ? d.name : (l.init && l.init.e ? show(l.init.e.l) : 'i');
      const st = l.step ? setParts({ s: 'expr', e: l.step }) : null;
      return [
        `This step repeats the steps inside it, counting with ${counter}${sentence ? ` (${sentence})` : ''}. In C, for (start; check; step) { } does it in one line: where the counter starts, the check that keeps it going, and how it changes each time round. Write only this line and its } — the steps inside are written on their own.`,
        'for (int ____ = ____; ____; ____) {\n}',
        [d ? `${counter} starts at ${d.init ? show(d.init) : '…'}: int ${counter} = ${d.init ? show(d.init) : '…'}` : '',
          l.c ? `It keeps going while ${show(l.c)}` : '',
          st ? `Each time round: ${st.written}` : ''].filter(Boolean).join('\n'),
        showMe,
      ];
    }
    return [
      `This step repeats the steps inside it as long as a check is true${sentence ? ` (${sentence})` : ''}. In C, while (check) { } checks first, then runs what is inside, then checks again. Write only this line and its } — the steps inside are written on their own.`,
      'while (____) {\n}',
      l && l.c ? `It keeps going while ${show(l.c)}` : '',
      showMe,
    ];
  }
  if (step.type === 'return') {
    const r = stmts[0];
    return [
      'This step ends the function here and gives a value back to the step that used it. In C, that is return.',
      r && r.e ? 'return ____;' : 'return;',
      r && r.e ? `The value given back is ${show(r.e)}` : 'Nothing is given back: return on its own.',
      showMe,
    ];
  }
  return [];
}

/** The step a node id names (or null). */
export const stepFor = (steps, nodeId) => (steps || []).find((s) => s.nodeId === nodeId) || null;

/** The next writable step not done, after `nodeId` in run order (wrapping). */
export function nextStep(steps, states, nodeId) {
  const w = (steps || []).filter((s) => s.writable);
  if (!w.length) return null;
  const at = w.findIndex((s) => s.nodeId === nodeId);
  for (let i = 1; i <= w.length; i += 1) {
    const s = w[(at + i + w.length) % w.length];
    if (stepStatus(states && states[s.nodeId], s) !== 'done') return s;
  }
  return null;
}

export { findNode };

/**
 * Gating: step-by-step Code Next shows only when the builder is NOT
 * words-only (the Problem Solving course never sees code), the stage is Code
 * Next, and the language is C with the v2 editors on.
 */
export function codeNextStepByStep({ stage, wordsOnly, language, v2 }) {
  return stage === 'code-next' && !wordsOnly && (language || 'c') === 'c' && !!v2;
}
